<template>
    <div class="flex flex-col gap-1" :class="placement === 'sidebar' ? 'w-full' : 'w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2 items-center'">
        <BaseButton
            theme="secondary"
            :class="{ 'w-full': placement === 'sidebar' }"
            data-testid="draft-self-review"
            :disabled="loading"
            class="flex items-center justify-center gap-2"
            @click="handleClick">
            <LoadingSpinner v-if="loading" class="h-4" />
            <i v-else class="bi bi-workspace" aria-hidden="true"></i>
            Draft my self-review
        </BaseButton>
        <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>

        <ChatSelfReviewDialog v-model:open="dialogOpen" :sections="sections" />
    </div>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import type { SelfReviewSectionDraft } from "~vue/types/performance_management";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { inject, ref } from "vue";

import ChatSelfReviewDialog from "./ChatSelfReviewDialog.vue";

interface Props {
    latestMessageId: number;
    /** ``sidebar`` fills the performance chat's right-hand panel; ``chat`` sits above the composer. */
    placement?: "chat" | "sidebar";
}

const props = withDefaults(defineProps<Props>(), { placement: "chat" });

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const noticeMessage = ref("");
const dialogOpen = ref(false);
const sections = ref<SelfReviewSectionDraft[]>([]);

async function handleClick() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}draft_self_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await execute(ChatAction.DRAFT_SELF_REVIEW_CARD, { message_id: props.latestMessageId }, { sync: true });
        if (result?.success && result.payload?.sections?.length) {
            sections.value = result.payload.sections;
            dialogOpen.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't draft your review. Please try again.";
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't draft your review. Please try again.";
    } finally {
        stopLoading();
    }
}
</script>
